import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Rocket } from "lucide-react";
import Navbar from "../components/Navbar";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import AuthSceneBackground from "../components/AuthSceneBackground";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { search } = useLocation();
  const [err, setErr] = useState(
    new URLSearchParams(search).get("google_error") ? "Google sign-in failed. Please try again." : ""
  );
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/levels");
    else setErr(res.error);
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="starfield" />
      <AuthSceneBackground />
      <Navbar />
      <main className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-24 pb-12 fade-in">
        <div className="w-full max-w-md">
          {/* Play-area style cyan neon frame matching the in-game canvas border */}
          <div className="relative">
            <div className="absolute -inset-1 border border-[#00ffff]/40 shadow-[0_0_24px_rgba(0,255,255,0.25)] pointer-events-none" />
            <form
              onSubmit={submit}
              className="relative glass p-8 border border-[#00ffff]/40 bg-[#05050a]/85 backdrop-blur-md"
              data-testid="login-form"
            >
              {/* Top HUD-like ribbon, mirrors the in-game HUD cards */}
              <div className="absolute -top-3 left-4 right-4 flex items-center justify-between">
                <div className="glass px-3 py-1 border border-[#00ffff]/50 flex items-center gap-2">
                  <Rocket className="w-3 h-3 text-[#00ffff]" />
                  <span className="font-mono-cosmic text-[9px] tracking-[0.35em] text-[#00ffff] uppercase">
                    Pilot · Auth
                  </span>
                </div>
                <div className="glass px-3 py-1 border border-[#ff4fb3]/40">
                  <span className="font-mono-cosmic text-[9px] tracking-[0.3em] text-[#ff4fb3]/90 uppercase">
                    Sector 01
                  </span>
                </div>
              </div>

              <div className="pt-4">
                <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-[#00ffff] uppercase mb-2">
                  // LOGIN SEQUENCE
                </div>
                <h1 className="font-heading font-black uppercase text-4xl tracking-tighter text-white mb-1">
                  Welcome back
                </h1>
                <p className="text-white/60 text-sm mb-8">
                  Login to resume your cosmic run.
                </p>

                <label className="block mb-4">
                  <span className="font-mono-cosmic text-[10px] tracking-[0.3em] text-white/60 uppercase block mb-2">
                    Email
                  </span>
                  <input
                    type="email"
                    className="input-cosmic"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </label>
                <label className="block mb-6">
                  <span className="font-mono-cosmic text-[10px] tracking-[0.3em] text-white/60 uppercase block mb-2">
                    Password
                  </span>
                  <input
                    type="password"
                    className="input-cosmic"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                  />
                </label>

                {err && (
                  <div
                    className="mb-4 px-3 py-2 border border-[#ff007f]/60 bg-[#ff007f]/10 text-[#ff007f] text-sm font-mono-cosmic"
                    data-testid="login-error"
                  >
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-neon cyan w-full disabled:opacity-50"
                  data-testid="login-submit-btn"
                >
                  {loading ? "Authenticating..." : "Launch"}
                </button>

                <GoogleSignInButton testId="login-google-btn" />

                <p className="mt-6 text-center text-sm text-white/60">
                  No account?{" "}
                  <Link
                    to="/register"
                    className="text-[#ff4fb3] hover:text-[#ff007f] transition"
                    data-testid="login-register-link"
                  >
                    Enlist now
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
